import { Box, SxProps, Theme, useMediaQuery } from "@mui/material";
import MuiButton, { ButtonTypeMap, ButtonProps as MuiButtonProps } from "@mui/material/Button";
import { OverrideProps } from "@mui/material/OverridableComponent";
import {
  ElementType,
  MouseEventHandler,
  MouseEvent,
  ReactElement,
  PropsWithChildren,
  useState,
} from "react";
import { FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE } from "../../DataDisplay/Icon/FaIcon";

export type ButtonProps<C extends React.ElementType = ButtonTypeMap["defaultComponent"]> =
  OverrideProps<ButtonTypeMap, C> & {
    component?: C;
    /**
     * The size to use on medium to large screens. NOTE: This will have no effect if the `size` prop is supplied
     * @default 'medium'
     */
    defaultSize?: MuiButtonProps["size"];
    /**
     * The size to use on small / extra small screens. NOTE: This will have no effect if the `size` prop is supplied
     * @default 'large'
     */
    smallSize?: MuiButtonProps["size"];
  };
type ClickHandler = (e: MouseEvent<unknown>) => void | Promise<void>;

const Icon = ({
  children,
  className,
  sx,
}: PropsWithChildren<{
  className?: string;
  sx?: SxProps<Theme>;
}>) => (
  <Box className={className} component="span" sx={{ display: "inherit", ...sx }}>
    {children}
  </Box>
);

export const Button = <C extends ElementType = "button">({
  size,
  defaultSize,
  smallSize = "large",
  startIcon,
  endIcon,
  children,
  sx,
  ...props
}: ButtonProps<C>): ReactElement => {
  const isSmallScreen = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const effectiveLoading = props.loading ?? loading;
  const effectiveSize = size || (isSmallScreen ? smallSize : defaultSize);

  const handleClick: MouseEventHandler<unknown> = async (event) => {
    if (props.onClick) {
      try {
        const result = (props.onClick as ClickHandler)(event);

        if (result instanceof Promise) {
          setLoading(true);
          await result;
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <MuiButton
      {...props}
      onClick={handleClick}
      loading={effectiveLoading}
      size={effectiveSize}
      sx={[{ [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {startIcon ? <Icon className="SrButton-startIcon">{startIcon}</Icon> : null}
      {children}
      {endIcon ? <Icon className="SrButton-endIcon">{endIcon}</Icon> : null}
    </MuiButton>
  );
};
